import {
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
} from '@material-ui/lab';
import { ColorDynamic, Stack } from '@superdispatch/ui';
import { TextBox } from '@superdispatch/ui-lab';
import { useField } from 'formik';
import styled from 'styled-components';
import { formatCurrency } from 'shared/helpers/IntlHelpers';
import {
  formatLoadExpenseChargeTo,
  getLoadExpenseChargeTo,
  getLoadExpenseChargeToFlags,
  LOAD_EXPENSE_CHARGE_TO_OPTIONS,
  LoadExpenseChargeTo,
} from '../data/LoadDTO';

const HelperTextBox = styled(TextBox)`
  max-width: 100%;
  overflow-wrap: break-word;
`;

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  width: 100%;

  & .MuiToggleButton-root {
    height: 32px;
    padding: 0 8px;
    box-sizing: border-box;
    border-width: 1px;
    border-color: ${ColorDynamic.Silver500};
    color: ${ColorDynamic.Dark300};
    text-transform: none;
    flex: 1 1 0;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &.Mui-selected {
      background-color: ${ColorDynamic.Blue50};
      color: ${ColorDynamic.Blue500};
    }
  }
`;

function formatChargeToHelperText(
  chargeTo: LoadExpenseChargeTo | undefined,
  price: number | null | undefined,
) {
  const amount = price ? formatCurrency(price) : 'This expense';

  switch (chargeTo) {
    case 'driver':
      return `${amount} will be deducted from driver pay.`;
    case 'shipper':
      return `${amount} will be added to the shipper invoice.`;
    case 'carrier':
      return `${amount} stays with the carrier — no invoice or driver deduction.`;
    default:
      return 'This expense has both "Show on invoice" and "Deduct from driver pay" set from before — pick who it should actually be charged to.';
  }
}

interface LoadExpenseChargeToFieldProps {
  namePrefix?: string;
  price?: number | null;
  compact?: boolean;
}

export function LoadExpenseChargeToField({
  namePrefix = '',
  price,
  compact = false,
}: LoadExpenseChargeToFieldProps) {
  const [showOnInvoiceField, , showOnInvoiceHelpers] = useField<
    boolean | undefined
  >(`${namePrefix}show_on_invoice`);
  const [deductFromDriverPayField, , deductFromDriverPayHelpers] = useField<
    boolean | undefined
  >(`${namePrefix}deduct_from_driver_pay`);

  const chargeTo = getLoadExpenseChargeTo({
    showOnInvoice: showOnInvoiceField.value,
    deductFromDriverPay: deductFromDriverPayField.value,
  });

  const handleChange: NonNullable<ToggleButtonGroupProps['onChange']> = (
    _,
    value: LoadExpenseChargeTo | null,
  ) => {
    if (!value) {
      return;
    }

    const flags = getLoadExpenseChargeToFlags(value);
    void showOnInvoiceHelpers.setValue(flags.showOnInvoice);
    void deductFromDriverPayHelpers.setValue(flags.deductFromDriverPay);
  };

  return (
    <Stack space="xsmall">
      {!compact && (
        <HelperTextBox color="secondary" display="block">
          Charge to
        </HelperTextBox>
      )}

      <StyledToggleButtonGroup
        exclusive={true}
        value={chargeTo ?? null}
        onChange={handleChange}
        aria-label="Charge to"
      >
        {LOAD_EXPENSE_CHARGE_TO_OPTIONS.map((option) => (
          <ToggleButton key={option} value={option}>
            {formatLoadExpenseChargeTo(option)}
          </ToggleButton>
        ))}
      </StyledToggleButtonGroup>

      {!compact && (
        <HelperTextBox color="secondary" variant="caption" display="block">
          {formatChargeToHelperText(chargeTo, price)}
        </HelperTextBox>
      )}
    </Stack>
  );
}
